import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useState, createContext, useContext, useMemo, useRef } from 'react';
import LayerSwapApiClient, { TransactionType } from '../lib/apiClients/layerSwapApiClient';
import useSWR from 'swr';
import useWallet from '../hooks/useWallet';
import { useSettingsState } from './settings';
import { transformSwapDataToQuoteArgs, useQuoteData } from '../hooks/useFee';
import { useRecentNetworksStore } from '../stores/recentRoutesStore';
import { useSelectedAccount } from './swapAccounts';
import { useInitialSettings } from './settings';
import { useSlippageStore } from '../stores/slippageStore';
import { useCallbacks } from './callbackProvider';
import { Address } from '../lib/address/Address';
import { useSwapTransactionStore } from '../stores';
import { resolveSwapPhase } from '../components/utils/resolveSwapPhase';
import { useContractAddressStore } from '../stores/contractAddressStore';
import { useExtendedSwapData } from '../hooks/useExtendedSwapDisplay';
import { useGaslessPreferenceStore } from '../stores/gaslessPreferenceStore';
import { isGaslessCapableRoute } from '../helpers/gasless';
import { resolveExtendedRoutePlan } from '../lib/extendedRoutes/registry';
import { buildCreateSwapParamsForExtendedRoute } from '../lib/extendedRoutes/transforms';
import { useExtendedRoutesStore } from '../stores/extendedRoutesStore';
import { isDepositAddressFlow, isDepositAddressSwap } from '../helpers/swapFlow';
import { resolveSwapPollingInterval, SWAP_POLL_DEDUPE_MS } from '../lib/swapPollingPolicy';
export const SwapDataStateContext = createContext(null);
export const SwapDataUpdateContext = createContext(null);
export function SwapDataProvider({ children, initialSwapData }) {
    const initialSettings = useInitialSettings();
    const { onSwapCreate } = useCallbacks();
    const [swapBasicFormData, setSwapBasicFormData] = useState();
    const { providers } = useWallet(swapBasicFormData?.source_network, 'asSource');
    const [quoteIsLoading, setQuoteLoading] = useState(false);
    const [withdrawType, setWithdrawType] = useState();
    const [depositAddressIsFromAccount, setDepositAddressIsFromAccount] = useState();
    // A pre-created swap (e.g. the deposit widget's prefetcher) seeds both the
    // id and, via SWR fallbackData below, the swap details — so consumers render
    // with data on first paint instead of a loading state.
    const [swapId, setSwapId] = useState(initialSettings.swapId?.toString() ?? initialSwapData?.swap.id);
    const [swapTransaction, setSwapTransaction] = useState();
    const { sourceRoutes, destinationRoutes, networks } = useSettingsState();
    const updateRecentTokens = useRecentNetworksStore(state => state.updateRecentNetworks);
    const [swapModalOpen, setSwapModalOpen] = useState(false);
    const [swapError, setSwapError] = useState(null);
    const quoteArgs = useMemo(() => transformSwapDataToQuoteArgs(swapBasicFormData, !!swapBasicFormData?.refuel), [swapBasicFormData]);
    // Deposit address flow doesn't use limits — min/max come from the detailed quote there
    const { quote: formDataQuote, quoteError: formDataQuoteError } = useQuoteData(quoteArgs, { refreshInterval: swapId ? 0 : undefined, skipLimits: isDepositAddressSwap(swapBasicFormData) });
    const handleUpdateSwapid = (value) => {
        setSwapId(value);
    };
    const setSubmitedFormValues = useCallback((values) => {
        if (!values.from || !values.to || !values.fromAsset || !values.toAsset || !values.destination_address)
            throw new Error("Form data is missing");
        if (!isDepositAddressFlow(values.depositMethod, values.fromExchange) && !values.amount)
            throw new Error("Form data is missing");
        setSwapBasicFormData({
            source_network: values.from,
            destination_network: values.to,
            source_token: values.fromAsset,
            destination_token: values.toAsset,
            requested_amount: values.amount || '',
            destination_address: values.destination_address,
            use_deposit_address: values.depositMethod === 'deposit_address',
            refuel: !!values.refuel,
            source_exchange: values.fromExchange,
        });
    }, [sourceRoutes, destinationRoutes]);
    const layerswapApiClient = new LayerSwapApiClient();
    const swap_details_endpoint = `/swaps/${swapId}?exclude_deposit_actions=true`;
    // Adaptive polling: burst-and-decay driven by swapPollingPolicy. The refreshInterval
    // callback identity must stay stable across renders (SWR resets its refresh loop when it
    // changes), so mutable inputs go through a ref — except the tx-submit timestamp, whose
    // identity change is intentional: it discards the stale slow timer and re-enters hot polling.
    const lastChangeRef = useRef({ at: Date.now() });
    const storedWalletTransaction = useSwapTransactionStore(state => swapId ? state.swapTransactions[swapId] : undefined);
    const computeRefreshInterval = useCallback((latestData) => {
        const swap = latestData?.data?.swap;
        // No usable payload (still loading, or an API error envelope): returning 0 here would
        // permanently stop SWR's refresh loop (a 0 from the function form never reschedules),
        // so keep it alive until a real payload arrives — slower when the API is erroring.
        if (!swap?.status)
            return latestData?.error ? 5000 : 1000;
        const { phase } = resolveSwapPhase({
            swapDetails: swap,
            refuel: latestData?.data?.refuel,
            storedWalletTransaction,
        });
        return resolveSwapPollingInterval({
            phase,
            now: Date.now(),
            lastChangeAt: lastChangeRef.current.at,
            txSubmittedAt: storedWalletTransaction?.timestamp,
            avgCompletionTime: latestData?.data?.quote?.avg_completion_time,
            isDepositAddressFlow: swap.use_deposit_address,
        });
    }, [storedWalletTransaction?.timestamp]);
    const { data, mutate, error } = useSWR(swapId ? swap_details_endpoint : null, layerswapApiClient.fetcher, { refreshInterval: computeRefreshInterval, dedupingInterval: SWAP_POLL_DEDUPE_MS, fallbackData: initialSwapData ? { data: initialSwapData } : undefined });
    const baseSwapData = useMemo(() => {
        if (!(swapId && data?.data?.swap))
            return undefined;
        const swap = data.data.swap;
        // Swap response omits supports_gasless_deposit; restore it from the route definition.
        const routeToken = sourceRoutes
            ?.find(r => r.name === swap.source_network?.name)
            ?.tokens?.find(t => t.symbol === swap.source_token?.symbol);
        const source_token = routeToken?.supports_gasless_deposit != null
            ? { ...swap.source_token, supports_gasless_deposit: routeToken.supports_gasless_deposit }
            : swap.source_token;
        return {
            ...swap,
            source_token,
            requested_amount: swap.requested_amount.toString(),
            refuel: !!data.data.refuel,
        };
    }, [data, swapId, sourceRoutes]);
    const extendedSwapData = useExtendedSwapData(swapId, baseSwapData, data?.data?.quote);
    const swapBasicData = useMemo(() => {
        if (swapId && data?.data) {
            if (!data.data.swap)
                return undefined;
            return extendedSwapData?.swapBasicData ?? baseSwapData;
        }
        return swapBasicFormData;
    }, [data, swapBasicFormData, swapId, baseSwapData, extendedSwapData]);
    const swapDetails = useMemo(() => {
        if (swapId)
            return data?.data?.swap;
    }, [data, swapId]);
    const quote = useMemo(() => {
        if (swapId && data?.data) {
            return extendedSwapData ? extendedSwapData.quote : data.data.quote;
        }
        return formDataQuote?.quote;
    }, [formDataQuote, data, swapId, extendedSwapData]);
    const quoteError = useMemo(() => {
        if (swapId && data?.data) {
            return undefined;
        }
        return formDataQuoteError;
    }, [formDataQuoteError, data, swapId]);
    const refuel = useMemo(() => {
        if (swapId && data?.data) {
            return data?.data?.refuel;
        }
        return formDataQuote?.refuel;
    }, [formDataQuote, data, swapId]);
    const selectedSourceAccount = useSelectedAccount("from", swapBasicFormData?.source_network?.name);
    const { wallets } = useWallet(swapBasicFormData?.source_network, 'asSource');
    const selectedWallet = (selectedSourceAccount?.address && swapBasicFormData) && wallets.find(w => Address.equals(w.address, selectedSourceAccount.address, swapBasicFormData?.source_network));
    const { checkContractStatus } = useContractAddressStore();
    const sourceIsSupported = (swapBasicData && selectedWallet) && WalletIsSupportedForSource({
        sourceNetwork: swapBasicData.source_network,
        sourceWallet: selectedWallet
    });
    const use_deposit_address = swapBasicData?.use_deposit_address;
    const deposit_actions_endpoint = swapId ? `/swaps/${swapId}/deposit_actions${(use_deposit_address || !selectedSourceAccount || !sourceIsSupported) ? "" : `?source_address=${selectedSourceAccount?.address}`}` : null;
    const inputTransfer = swapDetails?.transactions.find(t => t.type === TransactionType.Input);
    const { data: depositActions, error: depositActionsSwrError } = useSWR(!inputTransfer ? deposit_actions_endpoint : null, layerswapApiClient.fetcher);
    // The create-swap response may already carry deposit actions — use them as
    // a fallback (only while the seeded swap is still the active one) so the
    // deposit address renders without waiting for the separate fetch.
    const depositActionsResponse = depositActions?.data
        ?? (swapId && swapId === initialSwapData?.swap.id ? initialSwapData?.deposit_actions : undefined);
    const depositActionsError = depositActionsSwrError ? (depositActionsSwrError?.response?.data?.error?.message || 'Could not generate deposit address.') : undefined;
    // Track when the swap payload last changed — any movement restarts the hot polling window.
    useEffect(() => {
        const fingerprint = swapDataFingerprint(data?.data);
        if (fingerprint !== lastChangeRef.current.fingerprint) {
            lastChangeRef.current = { fingerprint, at: Date.now() };
        }
    }, [data]);
    // The race starts the moment the user's withdrawal tx is submitted — poll immediately
    // instead of waiting for the next scheduled tick. Skip txs persisted from a previous
    // session: on reload they'd just duplicate SWR's initial fetch (it still feeds
    // computeRefreshInterval via txSubmittedAt, so nothing else is lost).
    const mountedAtRef = useRef(Date.now());
    useEffect(() => {
        if (!swapId || !storedWalletTransaction)
            return;
        if (storedWalletTransaction.timestamp < mountedAtRef.current)
            return;
        mutate();
    }, [storedWalletTransaction?.timestamp, swapId]);
    // SWR pauses polling in hidden tabs; refresh as soon as the user comes back.
    useEffect(() => {
        if (!swapId)
            return;
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible')
                mutate();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [swapId, mutate]);
    useEffect(() => {
        if (!swapId)
            return;
        const data = JSON.parse(localStorage.getItem('swapTransactions') || "{}");
        const txForSwap = data.state?.swapTransactions?.[swapId];
        setSwapTransaction(txForSwap);
    }, [swapId]);
    const createSwap = useCallback(async (values, query, partner) => {
        if (!values)
            throw new Error("No swap data");
        const { to, fromAsset: fromCurrency, toAsset: toCurrency, from, refuel, fromExchange, depositMethod, amount, destination_address } = values;
        const depositAddressFlow = isDepositAddressFlow(depositMethod, fromExchange);
        if (!to || !fromCurrency || !toCurrency || !from || !destination_address || !depositMethod)
            throw new Error("Form data is missing");
        if (!depositAddressFlow && !amount)
            throw new Error("Form data is missing");
        const sourceWalletIsSupported = selectedWallet && WalletIsSupportedForSource({
            sourceNetwork: from,
            sourceWallet: selectedWallet
        });
        const contractCheckResult = (depositAddressFlow && selectedWallet) ? await checkContractStatus(selectedWallet.address, from, to) : null;
        const isContract = contractCheckResult?.sourceIsContract ?? false;
        const sourceIsSupported = sourceWalletIsSupported && !isContract;
        const slippage = useSlippageStore.getState().slippage;
        const gaslessEnabled = useGaslessPreferenceStore.getState().gaslessEnabled;
        const useGasless = isGaslessCapableRoute({
            depositMethod,
            supportsGaslessDeposit: fromCurrency.supports_gasless_deposit,
            sourceIsSupported: !!sourceIsSupported,
            sourceAddress: selectedSourceAccount?.address,
        }) && gaslessEnabled;
        const extendedPlan = resolveExtendedRoutePlan({
            sourceNetworkName: from.name,
            sourceTokenSymbol: fromCurrency.symbol,
            destinationNetworkName: to.name,
            destinationTokenSymbol: toCurrency.symbol,
            sourceAmount: amount,
            availableRoutes: sourceRoutes,
        });
        const isExtendedBridge = !!extendedPlan;
        const data = extendedPlan ? buildCreateSwapParamsForExtendedRoute({
            plan: extendedPlan,
            destinationNetworkName: to.name,
            destinationTokenSymbol: toCurrency.symbol,
            destinationAddress: destination_address,
            referenceId: query.externalId,
            refuel,
            sourceAddress: selectedSourceAccount?.address,
        }) : {
            amount: amount || undefined,
            source_network: from.name,
            destination_network: to.name,
            source_token: fromCurrency.symbol,
            destination_token: toCurrency.symbol,
            source_exchange: fromExchange?.name,
            destination_address: destination_address,
            reference_id: query.externalId,
            refuel: !!refuel,
            use_deposit_address: depositMethod === 'wallet' ? false : true,
            source_address: sourceIsSupported ? selectedSourceAccount?.address : undefined,
            refund_address: sourceIsSupported ? selectedSourceAccount?.address : undefined,
            ...(useGasless && { use_gasless: true, use_depository: true }),
        };
        if (!isExtendedBridge && depositMethod === 'wallet' && slippage && slippage > 0 && slippage < 0.8) {
            data.slippage = slippage.toString();
        }
        const swapResponse = await layerswapApiClient.CreateSwapAsync(data).catch((e) => {
            if (useGasless)
                useGaslessPreferenceStore.getState().reportGaslessUnavailable('create');
            throw e;
        });
        if (swapResponse?.error) {
            if (useGasless)
                useGaslessPreferenceStore.getState().reportGaslessUnavailable('create');
            throw swapResponse?.error;
        }
        const swap = swapResponse?.data;
        if (!swap?.swap.id)
            throw new Error("Could not create swap");
        onSwapCreate(swap);
        // Persist the extended identity so the post-create UI and the withdraw step
        // can keep showing the extended source and resume after a reload.
        if (extendedPlan) {
            useExtendedRoutesStore.getState().setRecord(swap.swap.id, {
                providerId: extendedPlan.mapping.provider.id,
                extendedNetwork: from.name,
                extendedToken: fromCurrency.symbol,
                realNetwork: extendedPlan.mapping.real.networkName,
                realToken: extendedPlan.mapping.real.tokenSymbol,
                sourceAddress: selectedSourceAccount?.address || '',
                sourceAmount: (amount || '').toString(),
                createdAt: Date.now(),
            });
        }
        updateRecentTokens({
            from: !fromExchange ? { network: from.name, token: fromCurrency.symbol } : undefined,
            to: { network: to.name, token: toCurrency.symbol }
        });
        return swap;
    }, [selectedSourceAccount, selectedWallet, onSwapCreate, updateRecentTokens, swapDetails?.id, networks, sourceRoutes]);
    const updateFns = useMemo(() => ({
        createSwap,
        mutateSwap: mutate,
        setDepositAddressIsFromAccount,
        setWithdrawType,
        setSwapId: handleUpdateSwapid,
        setSubmitedFormValues,
        setQuoteLoading,
        setSwapModalOpen
    }), [createSwap, mutate, handleUpdateSwapid, setSubmitedFormValues]);
    const stateValue = useMemo(() => ({
        withdrawType,
        swapTransaction,
        depositAddressIsFromAccount: !!depositAddressIsFromAccount,
        swapApiError: error,
        depositActionsResponse,
        depositActionsError,
        quote,
        quoteIsLoading,
        quoteError,
        refuel,
        swapBasicData,
        swapDetails,
        swapId,
        swapModalOpen,
        swapError,
        setSwapError
    }), [withdrawType, swapTransaction, depositAddressIsFromAccount, error, depositActionsResponse, depositActionsError, quote, quoteIsLoading, quoteError, refuel, swapBasicData, swapDetails, swapId, swapModalOpen, swapError]);
    return (_jsx(SwapDataStateContext.Provider, { value: stateValue, children: _jsx(SwapDataUpdateContext.Provider, { value: updateFns, children: children }) }));
}
export function useSwapDataState() {
    const data = useContext(SwapDataStateContext);
    if (data === undefined || data === null) {
        throw new Error('swapData must be used within a SwapDataProvider');
    }
    return data;
}
export function useSwapDataUpdate() {
    const updateFns = useContext(SwapDataUpdateContext);
    if (updateFns === undefined) {
        throw new Error('useSwapDataUpdate must be used within a SwapDataProvider');
    }
    return updateFns;
}
const swapDataFingerprint = (response) => {
    const swap = response?.swap;
    if (!swap)
        return undefined;
    return [
        swap.status,
        // Confirmed-state boolean, not the raw counter — confirmations climb on every block,
        // and counting each one as a payload change would re-arm hot polling indefinitely.
        ...(swap.transactions?.map(t => `${t.type}:${t.status}:${t.confirmations >= t.max_confirmations}:${t.transaction_hash}`) ?? []),
    ].join('|');
};
export const WalletIsSupportedForSource = ({ sourceNetwork, sourceWallet }) => {
    const isSupported = (sourceWallet && sourceWallet?.asSourceSupportedNetworks?.some(n => n === sourceNetwork?.name)) || false;
    return isSupported;
};
